import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSSE } from '../app/providers/SseProvider';
import {
  CREDENTIALS_VERIFICATION_UPDATED_SSE_EVENT,
  CREDENTIAL_STATUS_CHANGED_SSE_EVENT,
} from '@terra/shared-contracts-applications';
import { useProjectStore } from '../store/projectStore';

export function useCredentialVerificationSync({
  applicationId,
}: {
  applicationId?: string;
}) {
  const { subscribe } = useSSE();
  const queryClient = useQueryClient();
  const selectedCompany = useProjectStore((state) => state.selectedCompany);
  const tenantId = selectedCompany?.tenantId;

  useEffect(() => {
    if (!applicationId) return;

    const invalidateCredentialQueries = () => {
      queryClient.invalidateQueries({
        queryKey: ['browser-launcher-credentials', applicationId, tenantId],
      });
      queryClient.invalidateQueries({
        queryKey: ['cached-credentials', applicationId, tenantId],
      });
    };

    const unsubVerification = subscribe(
      CREDENTIALS_VERIFICATION_UPDATED_SSE_EVENT,
      (eventData) => {
        if (eventData.applicationId !== applicationId) return;
        invalidateCredentialQueries();
      },
    );

    const unsubStatusChanged = subscribe(
      CREDENTIAL_STATUS_CHANGED_SSE_EVENT,
      (eventData) => {
        if (eventData.applicationId !== applicationId) return;
        invalidateCredentialQueries();
      },
    );

    return () => {
      unsubVerification();
      unsubStatusChanged();
    };
  }, [applicationId, tenantId, subscribe, queryClient]);
}
